import { useState } from "react";
import { useTranslation } from 'react-i18next';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { MapPin, User, Sprout, Award } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface ProfileData {
  name: string;
  phone: string;
  location: string;
  farmSize: string;
  crops: string[];
  experienceLevel: string;
}

interface ProfileSetupProps {
  onComplete: (data: ProfileData) => void;
  onSkip: () => void;
}

const cropOptionKeys = [
  { value: "Broiler Chickens", key: "features.onboarding.crops.broilerChickens", defaultValue: "Broiler Chickens" },
  { value: "Layer Chickens", key: "features.onboarding.crops.layerChickens", defaultValue: "Layer Chickens" },
  { value: "Ducks", key: "features.onboarding.crops.ducks", defaultValue: "Ducks" },
  { value: "Rice", key: "features.onboarding.crops.rice", defaultValue: "Rice" },
  { value: "Vegetables", key: "features.onboarding.crops.vegetables", defaultValue: "Vegetables" },
  { value: "Fish", key: "features.onboarding.crops.fish", defaultValue: "Fish" },
  { value: "Cattle", key: "features.onboarding.crops.cattle", defaultValue: "Cattle" },
  { value: "Goats", key: "features.onboarding.crops.goats", defaultValue: "Goats" },
  { value: "Fruits", key: "features.onboarding.crops.fruits", defaultValue: "Fruits" },
  { value: "Other", key: "features.onboarding.crops.other", defaultValue: "Other" },
];

const experienceLevels = [
  { value: "beginner", labelKey: "features.onboarding.experience.beginner", labelDefault: "Beginner", descKey: "features.onboarding.experience.beginnerDesc", descDefault: "Less than 2 years" },
  { value: "intermediate", labelKey: "features.onboarding.experience.intermediate", labelDefault: "Intermediate", descKey: "features.onboarding.experience.intermediateDesc", descDefault: "2-5 years" },
  { value: "expert", labelKey: "features.onboarding.experience.expert", labelDefault: "Expert", descKey: "features.onboarding.experience.expertDesc", descDefault: "5+ years" }
];

export const ProfileSetup = ({ onComplete, onSkip }: ProfileSetupProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [data, setData] = useState<ProfileData>({
    name: "",
    phone: "",
    location: "",
    farmSize: "",
    crops: [],
    experienceLevel: ""
  });

  const totalSteps = 4;
  const progress = (step / totalSteps) * 100;

  const handleNext = () => {
    if (step === 1 && (!data.name || !data.phone)) {
      toast({
        title: t("features.onboarding.requiredFields", { defaultValue: "Required Fields" }),
        description: t("features.onboarding.requiredFieldsDesc", { defaultValue: "Please fill in your name and phone number" }),
        variant: "destructive"
      });
      return;
    }
    
    if (step < totalSteps) {
      setStep(step + 1);
    } else {
      onComplete(data);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const toggleCrop = (crop: string) => {
    setData(prev => ({
      ...prev,
      crops: prev.crops.includes(crop)
        ? prev.crops.filter(c => c !== crop)
        : [...prev.crops, crop]
    }));
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <div className="px-6 pt-6 pb-4">
        <h2 className="text-2xl font-bold text-foreground mb-2">
          {t('features.onboarding.setUpProfile')}
        </h2>
        <p className="text-sm text-muted-foreground mb-4">
          {t('features.onboarding.stepOf', { step, total: totalSteps })}
        </p>
        <Progress value={progress} className="h-2" />
      </div>

      {/* Content */}
      <div className="flex-1 px-6 py-6 overflow-y-auto">
        {step === 1 && (
          <div className="space-y-6 animate-fade-in">
            <div className="flex items-center gap-2 mb-4">
              <User className="w-5 h-5 text-primary" />
              <h3 className="text-lg font-semibold text-foreground">{t('features.onboarding.basicInformation')}</h3>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="name">{t('features.onboarding.fullName')}</Label>
              <Input
                id="name"
                value={data.name}
                onChange={(e) => setData({ ...data, name: e.target.value })}
                placeholder={t("features.onboarding.namePlaceholder", { defaultValue: "Enter your name" })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">{t('features.onboarding.phoneNumber')}</Label>
              <Input
                id="phone"
                type="tel"
                value={data.phone}
                onChange={(e) => setData({ ...data, phone: e.target.value })}
                placeholder="+254 XXX XXX XXX"
              />
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-6 animate-fade-in">
            <div className="flex items-center gap-2 mb-4">
              <MapPin className="w-5 h-5 text-primary" />
              <h3 className="text-lg font-semibold text-foreground">{t('profile.location')}</h3>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="location">{t('features.onboarding.farmLocation')}</Label>
              <Input
                id="location"
                value={data.location}
                onChange={(e) => setData({ ...data, location: e.target.value })}
                placeholder={t("features.onboarding.locationPlaceholder", { defaultValue: "City, County, or Region" })}
              />
              <p className="text-xs text-muted-foreground">
                {t('features.onboarding.locationHelp')}
              </p>
            </div>

            <div className="w-full h-48 rounded-lg bg-muted flex items-center justify-center">
              <MapPin className="w-12 h-12 text-muted-foreground" />
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-6 animate-fade-in">
            <div className="flex items-center gap-2 mb-4">
              <Sprout className="w-5 h-5 text-primary" />
              <h3 className="text-lg font-semibold text-foreground">{t('features.onboarding.farmDetails')}</h3>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="farmSize">{t('features.onboarding.farmSizeAcres')}</Label>
              <Input
                id="farmSize"
                type="number"
                value={data.farmSize}
                onChange={(e) => setData({ ...data, farmSize: e.target.value })}
                placeholder="e.g., 2.5"
              />
            </div>

            <div className="space-y-3">
              <Label>{t('settings.primaryCrops')}</Label>
              <div className="grid grid-cols-2 gap-3">
                {cropOptionKeys.map((crop) => (
                  <div key={crop.value} className="flex items-center space-x-2">
                    <Checkbox
                      id={crop.value}
                      checked={data.crops.includes(crop.value)}
                      onCheckedChange={() => toggleCrop(crop.value)}
                    />
                    <label
                      htmlFor={crop.value}
                      className="text-sm text-foreground cursor-pointer"
                    >
                      {t(crop.key, { defaultValue: crop.defaultValue })}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-6 animate-fade-in">
            <div className="flex items-center gap-2 mb-4">
              <Award className="w-5 h-5 text-primary" />
              <h3 className="text-lg font-semibold text-foreground">{t('features.onboarding.experienceLevel')}</h3>
            </div>
            
            <div className="space-y-3">
              {experienceLevels.map((level) => (
                <button
                  key={level.value}
                  onClick={() => setData({ ...data, experienceLevel: level.value })}
                  className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                    data.experienceLevel === level.value
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-primary/50"
                  }`}
                >
                  <div className="font-semibold text-foreground">{t(level.labelKey, { defaultValue: level.labelDefault })}</div>
                  <div className="text-sm text-muted-foreground">{t(level.descKey, { defaultValue: level.descDefault })}</div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="px-6 pb-8 space-y-3">
        <div className="flex gap-3">
          {step > 1 && (
            <Button onClick={handleBack} variant="outline" size="lg" className="flex-1">
              {t('common.back')}
            </Button>
          )}
          <Button onClick={handleNext} variant="primary" size="lg" className="flex-1">
            {step === totalSteps ? t('features.onboarding.complete') : t('features.onboarding.continue')}
          </Button>
        </div>
        
        <Button onClick={onSkip} variant="ghost" size="lg" className="w-full">
          {t('features.onboarding.skipForNow')}
        </Button>
      </div>
    </div>
  );
};
